import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Modal } from '@/components/ui'
import {
  archiveDocumentSchema,
  type ArchiveDocumentFormData,
} from '../schemas/archiveDocument.schema'

interface ArchiveDocumentModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (archiveReason?: string) => void
  isPending?: boolean
  documentName?: string
}

export function ArchiveDocumentModal({
  isOpen,
  onClose,
  onConfirm,
  isPending = false,
  documentName,
}: ArchiveDocumentModalProps): React.ReactElement {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ArchiveDocumentFormData>({
    resolver: zodResolver(archiveDocumentSchema),
    defaultValues: { archiveReason: '' },
  })

  const handleClose = (): void => {
    reset()
    onClose()
  }

  const onSubmit = (data: ArchiveDocumentFormData): void => {
    onConfirm(data.archiveReason || undefined)
    reset()
  }

  return (
    <Modal open={isOpen} onClose={handleClose} title="Archive document">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {documentName ? (
            <>
              Archive <span className="font-medium text-gray-900 dark:text-gray-100">{documentName}</span>?{' '}
            </>
          ) : (
            'Archive this document? '
          )}
          The file will be retained and can be restored later.
        </p>

        <div>
          <label
            htmlFor="archiveReason"
            className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Reason <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            id="archiveReason"
            rows={3}
            maxLength={500}
            placeholder="e.g. Employee offboarded — retain for 7 years"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
            {...register('archiveReason')}
          />
          {errors.archiveReason && (
            <p className="mt-1 text-sm text-red-600">{errors.archiveReason.message}</p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" loading={isPending}>
            {isPending ? 'Archiving…' : 'Archive'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

ArchiveDocumentModal.displayName = 'ArchiveDocumentModal'
